import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { ROUTES } from '../../constants/routes';
import transparentLogo from '../../assets/images/Transparent_logo.png';
import {
  GameControllerIcon,
  TrophyIcon,
  ChurchIcon,
  BibleIcon,
  BlogIcon,
  SupportIcon,
  VolunteerIcon,
  HeartIcon
} from '../common/Icons';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Close user dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  const initials = user?.displayName
    ? user.displayName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : (user?.email ? user.email.slice(0, 2).toUpperCase() : '?');

  return (
    <nav className="navbar">
      {/* Brand Logo & Title */}
      <div className="navbar__brand-wrap">
        <span
          className="navbar__brand"
          onClick={() => navigate(ROUTES.PUBLIC.HOME)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <img src={transparentLogo} alt="Bible Contest Logo" className="navbar-logo-img" />
          <span>Bible Contest</span>
        </span>
      </div>

      {/* Desktop Navigation Links */}
      <div className="navbar__nav-links">
        {/* Group 1: Gameplay & Host Hub - Only visible when logged in */}
        {user && (
          <>
            <button
              type="button"
              className={`nav-link-btn ${pathname.startsWith('/play') || pathname === '/games' ? 'active' : ''}`}
              onClick={() => navigate(ROUTES.GAMES.PLAY)}
              style={{
                fontWeight: 800,
                color: pathname.startsWith('/play') || pathname === '/games' ? 'var(--clr-primary)' : 'var(--clr-text-1)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <GameControllerIcon size={18} style={{ color: 'var(--clr-primary)' }} />
              <span>Play Games</span>
              <span style={{ fontSize: '10px', background: '#FFF4F3', color: '#FF5C56', padding: '1px 6px', borderRadius: '4px', fontWeight: 800 }}>LIVE</span>
            </button>

            <button
              type="button"
              className={`nav-link-btn ${pathname === ROUTES.GAMES.GLOBAL_LEADERBOARD ? 'active' : ''}`}
              onClick={() => navigate(ROUTES.GAMES.GLOBAL_LEADERBOARD)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <TrophyIcon size={17} style={{ color: '#D97706' }} />
              <span>Ranking</span>
            </button>

            <button
              type="button"
              className={`nav-link-btn ${pathname.startsWith(ROUTES.PUBLIC.STUDIO) ? 'active' : ''}`}
              onClick={() => navigate(ROUTES.PUBLIC.STUDIO)}
            >
              Host Studio
            </button>

            {/* Visual Group Divider */}
            <div
              style={{
                width: '1px',
                height: '18px',
                backgroundColor: 'var(--clr-border, #E4E4E7)',
                margin: '0 2px',
              }}
            />
          </>
        )}

        {/* Group 2: Mission & Community */}
        <button
          type="button"
          className={`nav-link-btn ${pathname === ROUTES.PUBLIC.HOME ? 'active' : ''}`}
          onClick={() => navigate(ROUTES.PUBLIC.HOME)}
        >
          About Us
        </button>

        <button
          type="button"
          className={`nav-link-btn ${pathname.startsWith(ROUTES.PUBLIC.BLOG) ? 'active' : ''}`}
          onClick={() => navigate(ROUTES.PUBLIC.BLOG)}
        >
          Blog
        </button>

        <button
          type="button"
          className={`nav-link-btn ${pathname.startsWith(ROUTES.PUBLIC.SUPPORT) ? 'active' : ''}`}
          onClick={() => navigate(ROUTES.PUBLIC.SUPPORT)}
        >
          Support
        </button>

        <button
          type="button"
          className={`nav-link-btn ${pathname.startsWith(ROUTES.PUBLIC.VOLUNTEER) || pathname.startsWith(ROUTES.PUBLIC.CAREERS) ? 'active' : ''}`}
          onClick={() => navigate(ROUTES.PUBLIC.VOLUNTEER)}
        >
          Volunteer With Us
        </button>

        <button
          type="button"
          className={`nav-link-btn ${pathname.startsWith(ROUTES.PUBLIC.DONATE) || pathname.startsWith(ROUTES.PUBLIC.FUNDRAISING) ? 'active' : ''}`}
          onClick={() => navigate(ROUTES.PUBLIC.DONATE)}
        >
          Support Mission
        </button>
      </div>

      {/* User Actions / Sign In / Mobile Toggle */}
      <div className="navbar__actions" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
        {user ? (
          <div ref={userMenuRef} style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setUserMenuOpen((prev) => !prev)}
              aria-label="User profile menu"
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'var(--clr-primary, #FF5C56)',
                color: '#ffffff',
                border: '2px solid rgba(255, 255, 255, 0.8)',
                boxShadow: '0 2px 6px rgba(255, 92, 86, 0.3)',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.15s ease'
              }}
            >
              {initials}
            </button>

            {userMenuOpen && (
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 8px)',
                  minWidth: '220px',
                  background: 'var(--clr-surface, #ffffff)',
                  border: '1px solid var(--clr-border, #E4E4E7)',
                  borderRadius: '16px',
                  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.04)',
                  padding: '12px',
                  zIndex: 1000,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {/* User Info Header */}
                <div style={{ padding: '4px 6px 8px 6px', borderBottom: '1px solid var(--clr-border, #E4E4E7)' }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--clr-text, #18181B)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.displayName || 'Contest Host'}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--clr-text-2, #71717A)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '2px' }}>
                    {user.email}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate(ROUTES.USER.PROFILE);
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 10px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: 'var(--clr-text, #18181B)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'background 0.15s ease'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--clr-surface-container, #F5EEE3)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ fontSize: '15px' }}>👤</span>
                    <span>My Profile & Stats</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate(ROUTES.PUBLIC.STUDIO);
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 10px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: 'var(--clr-text, #18181B)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'background 0.15s ease'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--clr-surface-container, #F5EEE3)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <ChurchIcon size={16} style={{ color: 'var(--clr-primary)' }} />
                    <span>Host Studio</span>
                  </button>
                </div>

                {/* Sign Out */}
                <div style={{ borderTop: '1px solid var(--clr-border, #E4E4E7)', paddingTop: '6px' }}>
                  <button
                    type="button"
                    onClick={async () => {
                      setUserMenuOpen(false);
                      await logout();
                      navigate(ROUTES.PUBLIC.HOME);
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 10px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#DC2626',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'background 0.15s ease'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#FEF2F2'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span>🚪</span>
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            className="navbar-login-btn"
            onClick={() => navigate(ROUTES.AUTH.LOGIN)}
            style={{
              padding: '8px 18px',
              borderRadius: '9999px',
              border: 'none',
              background: 'var(--clr-primary, #FF5C56)',
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'opacity 0.15s ease'
            }}
          >
            Sign In
          </button>
        )}

        {/* Mobile / Tablet Menu Button on Right Top Corner */}
        <button
          type="button"
          className="navbar__menu-btn"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open Navigation Menu"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </div>

      {/* Side Navigation Drawer Overlay Backdrop */}
      <div
        className={`side-nav-backdrop ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Side Navigation Drawer Panel */}
      <aside className={`side-nav-drawer ${mobileMenuOpen ? 'open' : ''}`} aria-label="Mobile Navigation">
        {/* Drawer Header */}
        <div className="side-nav-header">
          <div
            className="side-nav-brand"
            onClick={() => {
              setMobileMenuOpen(false);
              navigate(ROUTES.PUBLIC.HOME);
            }}
          >
            <img src={transparentLogo} alt="Bible Contest Logo" className="side-nav-logo" />
            <span>Bible Contest</span>
          </div>

          <button
            type="button"
            className="side-nav-close-btn"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close Navigation"
          >
            ✕
          </button>
        </div>

        {/* Drawer Content */}
        <div className="side-nav-body">
          {/* User Profile or Guest CTA */}
          {user ? (
            <div
              className="side-nav-user-card"
              onClick={() => {
                setMobileMenuOpen(false);
                navigate(ROUTES.USER.PROFILE);
              }}
              style={{ cursor: 'pointer' }}
              title="View your profile & stats"
            >
              <div className="side-nav-user-avatar">{initials}</div>
              <div className="side-nav-user-info">
                <div className="side-nav-user-name">{user.displayName || 'Contest Host'}</div>
                <div className="side-nav-user-email">{user.email}</div>
              </div>
              <span style={{ marginLeft: 'auto', color: 'var(--clr-text-2)', fontSize: '13px' }}>→</span>
            </div>
          ) : (
            <div className="side-nav-guest-card">
              <div>
                <div style={{ fontWeight: 700, fontSize: '13.5px', color: '#18181B' }}>Welcome!</div>
                <div style={{ fontSize: '12px', color: '#71717A', marginTop: '2px' }}>
                  Sign in to play and host contests
                </div>
              </div>
              <button
                type="button"
                className="side-nav-login-btn"
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate(ROUTES.AUTH.LOGIN);
                }}
              >
                Sign In
              </button>
            </div>
          )}

          {/* Group 1: Game & Host Hub - Only visible when logged in */}
          {user && (
            <>
              <div className="side-nav-group-title">GAMES & CONTESTS</div>

              <button
                type="button"
                className={`side-nav-link ${pathname === ROUTES.USER.PROFILE ? 'active' : ''}`}
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate(ROUTES.USER.PROFILE);
                }}
              >
                <span className="side-nav-icon">👤</span>
                <span className="side-nav-label">My Profile & Stats</span>
              </button>

              <button
                type="button"
                className={`side-nav-link ${pathname.startsWith('/play') || pathname === '/games' ? 'active' : ''}`}
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate(ROUTES.GAMES.PLAY);
                }}
              >
                <span className="side-nav-icon"><GameControllerIcon size={18} style={{ color: 'var(--clr-primary)' }} /></span>
                <span className="side-nav-label">Play Games</span>
                <span className="side-nav-live-badge">LIVE</span>
              </button>

              <button
                type="button"
                className={`side-nav-link ${pathname === ROUTES.GAMES.GLOBAL_LEADERBOARD ? 'active' : ''}`}
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate(ROUTES.GAMES.GLOBAL_LEADERBOARD);
                }}
              >
                <span className="side-nav-icon"><TrophyIcon size={18} style={{ color: '#D97706' }} /></span>
                <span className="side-nav-label">Ranking</span>
              </button>

              <button
                type="button"
                className={`side-nav-link ${pathname.startsWith(ROUTES.PUBLIC.STUDIO) ? 'active' : ''}`}
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate(ROUTES.PUBLIC.STUDIO);
                }}
              >
                <span className="side-nav-icon"><ChurchIcon size={18} style={{ color: '#C08552' }} /></span>
                <span className="side-nav-label">Host Studio</span>
              </button>

              <div className="side-nav-divider" />
            </>
          )}

          {/* Group 2: Mission & Ministry */}
          <div className="side-nav-group-title">COMMUNITY & MISSION</div>

          <button
            type="button"
            className={`side-nav-link ${pathname === ROUTES.PUBLIC.HOME ? 'active' : ''}`}
            onClick={() => {
              setMobileMenuOpen(false);
              navigate(ROUTES.PUBLIC.HOME);
            }}
          >
            <span className="side-nav-icon"><BibleIcon size={18} style={{ color: 'var(--clr-primary)' }} /></span>
            <span className="side-nav-label">About Us</span>
          </button>

          <button
            type="button"
            className={`side-nav-link ${pathname.startsWith(ROUTES.PUBLIC.BLOG) ? 'active' : ''}`}
            onClick={() => {
              setMobileMenuOpen(false);
              navigate(ROUTES.PUBLIC.BLOG);
            }}
          >
            <span className="side-nav-icon"><BlogIcon size={18} style={{ color: '#059669' }} /></span>
            <span className="side-nav-label">Blog</span>
          </button>

          <button
            type="button"
            className={`side-nav-link ${pathname.startsWith(ROUTES.PUBLIC.SUPPORT) ? 'active' : ''}`}
            onClick={() => {
              setMobileMenuOpen(false);
              navigate(ROUTES.PUBLIC.SUPPORT);
            }}
          >
            <span className="side-nav-icon"><SupportIcon size={18} style={{ color: '#2563EB' }} /></span>
            <span className="side-nav-label">Support</span>
          </button>

          <button
            type="button"
            className={`side-nav-link ${pathname.startsWith(ROUTES.PUBLIC.VOLUNTEER) || pathname.startsWith(ROUTES.PUBLIC.CAREERS) ? 'active' : ''}`}
            onClick={() => {
              setMobileMenuOpen(false);
              navigate(ROUTES.PUBLIC.VOLUNTEER);
            }}
          >
            <span className="side-nav-icon"><VolunteerIcon size={18} style={{ color: '#4F46E5' }} /></span>
            <span className="side-nav-label">Volunteer With Us</span>
          </button>

          <button
            type="button"
            className={`side-nav-link ${pathname.startsWith(ROUTES.PUBLIC.DONATE) || pathname.startsWith(ROUTES.PUBLIC.FUNDRAISING) ? 'active' : ''}`}
            onClick={() => {
              setMobileMenuOpen(false);
              navigate(ROUTES.PUBLIC.DONATE);
            }}
          >
            <span className="side-nav-icon"><HeartIcon size={18} style={{ color: '#E11D48' }} /></span>
            <span className="side-nav-label">Support Mission</span>
          </button>

          {/* Sign Out Option for Authenticated Users */}
          {user && (
            <>
              <div className="side-nav-divider" />
              <button
                type="button"
                className="side-nav-signout-btn"
                onClick={async () => {
                  setMobileMenuOpen(false);
                  await logout();
                  navigate(ROUTES.PUBLIC.HOME);
                }}
              >
                <span>🚪</span>
                <span>Sign Out</span>
              </button>
            </>
          )}
        </div>

        {/* Drawer Footer */}
        <div className="side-nav-footer">
          <p>"Thy word is a lamp unto my feet, and a light unto my path."</p>
          <small>— Psalm 119:105</small>
        </div>
      </aside>
    </nav>
  );
};

export default Navbar;
